import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Utensils, Clock, Phone, GraduationCap, Award, Play, X, Pause, Volume2, VolumeX, Maximize2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { trackWhatsAppClick } from '../../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EtiquetteVideoModal = ({ isOpen, onClose, videoUrl }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (isOpen && videoRef.current) {
            videoRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        }
    }, [isOpen]);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) videoRef.current.pause(); else videoRef.current.play();
            setIsPlaying(!isPlaying);
        }
    };
    const toggleMute = () => {
        if (videoRef.current) { videoRef.current.muted = !isMuted; setIsMuted(!isMuted); }
    };
    const toggleFullscreen = () => {
        const el = document.getElementById('etiquette-video-container');
        if (el) { if (!document.fullscreenElement) el.requestFullscreen(); else document.exitFullscreen(); }
    };
    const handleTimeUpdate = () => {
        if (videoRef.current) setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
    };
    const handleSeek = (e) => {
        if (videoRef.current) {
            const rect = e.currentTarget.getBoundingClientRect();
            videoRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * videoRef.current.duration;
        }
    };
    const handleClose = () => {
        if (videoRef.current) videoRef.current.pause();
        setIsPlaying(false); setProgress(0); onClose();
    };

    if (!isOpen) return null;

    const isYouTube = videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be');
    let youtubeEmbedUrl = '';
    if (isYouTube) {
        let videoId = '';
        if (videoUrl.includes('youtu.be/')) {
            videoId = videoUrl.split('youtu.be/')[1]?.split(/[?&#]/)[0];
        } else {
            const urlParams = new URLSearchParams(videoUrl.split('?')[1]);
            videoId = urlParams.get('v');
        }
        youtubeEmbedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }

    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black" onClick={handleClose}
            >
                <button onClick={handleClose}
                    className="fixed top-4 right-4 z-[60] w-12 h-12 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors"
                    data-testid="close-etiquette-video-modal"
                >
                    <X className="w-6 h-6" />
                </button>
                <div className="fixed top-4 left-4 z-[60]">
                    <h3 className="text-white font-display text-lg drop-shadow-lg">Standar Etiket Makan di Spencer Green</h3>
                </div>
                <motion.div id="etiquette-video-container"
                    initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className="relative w-full h-full" onClick={(e) => e.stopPropagation()}
                >
                    <div className="relative bg-black w-full h-full flex items-center justify-center">
                        {isYouTube ? (
                            <iframe src={youtubeEmbedUrl} className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen title="Standar Etiket Makan" />
                        ) : (
                            <>
                                <video ref={videoRef} src={videoUrl} className="w-full h-full object-contain"
                                    onTimeUpdate={handleTimeUpdate} onEnded={() => setIsPlaying(false)} playsInline
                                    data-testid="etiquette-video" />
                                <div className="absolute inset-0 flex items-center justify-center group">
                                    <button onClick={togglePlay}
                                        className="w-20 h-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100">
                                        {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                                    </button>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-3" onClick={handleSeek}>
                                        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <button onClick={togglePlay} className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors">
                                                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                                            </button>
                                            <button onClick={toggleMute} className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors">
                                                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                            </button>
                                        </div>
                                        <button onClick={toggleFullscreen} className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors">
                                            <Maximize2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

const TableManner = () => {
    const [content, setContent] = useState({});
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [etiquetteVideoUrl, setEtiquetteVideoUrl] = useState('');

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await axios.get(`${API_URL}/content/table-manner`);
                const contentMap = {};
                response.data.forEach(item => {
                    contentMap[item.section] = item.content;
                });
                setContent(contentMap);
                // Extract etiquette video URL
                const etiquetteContent = response.data.find(item => item.section === 'etiquette_video');
                if (etiquetteContent?.content?.video_url) {
                    setEtiquetteVideoUrl(etiquetteContent.content.video_url);
                }
            } catch (error) {
                console.error('Error fetching content:', error);
            }
        };
        fetchContent();
    }, []);

    // Default packages
    const defaultPackages = [
        {
            name: 'Basic Table Manner',
            pax: '20 - 30 pax',
            price: 'Rp 150.000/pax',
            includes: ['Materi Etika Makan', 'Praktek Langsung', 'Welcome Drink', 'Sertifikat'],
            image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800'
        },
        {
            name: 'Premium Table Manner',
            pax: '20 - 50 pax',
            price: 'Rp 250.000/pax',
            includes: ['Materi Etika Makan', 'Praktek 3-Course Meal', 'Coffee Break', 'Lunch', 'Sertifikat', 'Dokumentasi'],
            image: 'https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=800'
        },
        {
            name: 'Exclusive Table Manner',
            pax: '30 - 100 pax',
            price: 'Rp 350.000/pax',
            includes: ['Materi Lengkap', '5-Course Meal', 'Coffee Break', 'Wine Tasting Demo', 'Sertifikat', 'Dokumentasi', 'Souvenir'],
            image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800'
        }
    ];

    // Get packages from API or defaults
    const packages = [1, 2, 3].map((num, index) => {
        const apiPkg = content[`package${num}`];
        if (apiPkg && apiPkg.name) {
            return {
                name: apiPkg.name,
                pax: apiPkg.pax || defaultPackages[index].pax,
                price: apiPkg.price || defaultPackages[index].price,
                includes: apiPkg.includes ? apiPkg.includes.split('\n').map(i => i.trim()).filter(i => i) : defaultPackages[index].includes,
                image: apiPkg.image || defaultPackages[index].image
            };
        }
        return defaultPackages[index];
    });

    // Hero content
    const heroContent = content.hero || {};

    // Benefits
    const benefits = [
        { icon: GraduationCap, title: 'Instruktur Profesional', desc: 'Dibimbing oleh hospitality expert berpengalaman' },
        { icon: Utensils, title: 'Praktek Langsung', desc: 'Belajar dengan hidangan asli bukan simulasi' },
        { icon: Award, title: 'Sertifikat Resmi', desc: 'Sertifikat kelulusan untuk setiap peserta' },
        { icon: Users, title: 'Grup Fleksibel', desc: 'Cocok untuk sekolah, kampus, dan korporat' }
    ];

    // Handle Request Quote
    const handleRequestQuote = (pkgName) => {
        trackWhatsAppClick(`Table Manner Quote - ${pkgName}`);
        const message = `Hi, saya ingin bertanya tentang paket ${pkgName} untuk Table Manner.`;
        const url = `https://wa.me/6281130700206?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };

    return (
        <div className="bg-emerald-50/30">
            {/* Hero */}
            <section className="relative h-[100svh] min-h-[600px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    {heroContent.video ? (
                        (() => {
                            const isYouTube = heroContent.video.includes('youtube.com') || heroContent.video.includes('youtu.be');
                            if (isYouTube) {
                                let videoId = '';
                                if (heroContent.video.includes('youtu.be/')) {
                                    videoId = heroContent.video.split('youtu.be/')[1]?.split(/[?&#]/)[0];
                                } else {
                                    const urlParams = new URLSearchParams(heroContent.video.split('?')[1]);
                                    videoId = urlParams.get('v');
                                }
                                return (
                                    <div className="w-full h-full overflow-hidden">
                                        <iframe
                                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
                                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto"
                                            style={{ aspectRatio: '16/9', pointerEvents: 'none' }}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            title="Hero Video"
                                            frameBorder="0"
                                        />
                                    </div>
                                );
                            }
                            return (
                                <video
                                    key={heroContent.video}
                                    src={heroContent.video}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="auto"
                                    className="w-full h-full object-cover"
                                    poster={heroContent.image || undefined}
                                />
                            );
                        })()
                    ) : (
                        <div
                            className="w-full h-full bg-cover bg-center"
                            style={{ backgroundImage: `url(${heroContent.image || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920'})` }}
                        />
                    )}
                    <div className="absolute inset-0 bg-black/40" />
                </div>
                <div className="relative text-center px-4">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <p className="text-emerald-300 uppercase tracking-widest text-sm mb-2">{heroContent.subtitle || 'Etiquette Training'}</p>
                        <h1 className="sr-only">Paket Table Manner Spencer Green Hotel Batu</h1>
                        <p className="font-display text-4xl sm:text-5xl text-white mb-4" role="heading" aria-level="2">
                            {heroContent.title || 'Paket Table Manner'}
                        </p>
                        <p className="text-emerald-100 max-w-2xl mx-auto text-lg">
                            {heroContent.description || 'Pelajari etika makan internasional dengan suasana hotel bintang yang nyaman'}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Etiquette Video Section */}
            <section className="py-12 bg-gradient-to-r from-emerald-800 to-emerald-900">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8"
                    >
                        <div className="flex-1 text-center md:text-left">
                            <p className="text-emerald-300 uppercase tracking-widest text-xs mb-2">Video Standar Kami</p>
                            <h2 className="font-display text-2xl md:text-3xl font-medium text-white leading-snug">
                                Standar Etiket Makan yang Kami Ajarkan di Spencer Green
                            </h2>
                            <p className="text-emerald-200/80 mt-2 text-sm">Lihat langsung bagaimana pelatihan kami berlangsung</p>
                        </div>
                        <button
                            onClick={() => {
                                if (etiquetteVideoUrl) {
                                    setShowVideoModal(true);
                                } else {
                                    alert('Video belum tersedia. Hubungi admin untuk menambahkan video.');
                                }
                            }}
                            className="group flex items-center gap-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-emerald-900/30"
                            data-testid="etiquette-video-btn"
                        >
                            <div className="w-12 h-12 bg-emerald-500 group-hover:bg-emerald-400 rounded-full flex items-center justify-center transition-colors">
                                <Play className="w-6 h-6 text-white ml-0.5" />
                            </div>
                            <span className="font-semibold text-lg">Tonton Video</span>
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p className="text-emerald-600 uppercase tracking-widest text-sm mb-2">Keunggulan</p>
                        <h2 className="font-display text-3xl font-medium text-gray-900">Mengapa Pilih Kami?</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={benefit.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center p-4"
                            >
                                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <benefit.icon className="w-7 h-7 text-emerald-600" />
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                                <p className="text-gray-500 text-sm">{benefit.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Packages */}
            <section className="py-20 bg-emerald-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <p className="text-emerald-600 uppercase tracking-widest text-sm mb-2">Pilihan Paket</p>
                        <h2 className="font-display text-4xl font-medium text-gray-900">Paket Table Manner</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {packages.map((pkg, index) => (
                            <motion.div
                                key={pkg.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white rounded-xl overflow-hidden shadow-soft flex flex-col"
                                data-testid={`table-manner-package-${index}`}
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="p-6 flex flex-col flex-1">
                                    <h3 className="font-display text-xl font-semibold text-gray-900 mb-2">{pkg.name}</h3>
                                    <div className="flex items-center text-gray-500 mb-2">
                                        <Users className="w-4 h-4 mr-2" />
                                        <span className="text-sm">{pkg.pax}</span>
                                    </div>
                                    <p className="text-emerald-600 font-medium text-xl mb-4">{pkg.price}</p>
                                    <ul className="space-y-2 mb-6 flex-1">
                                        {pkg.includes.map((item, i) => (
                                            <li key={i} className="text-gray-600 text-sm flex items-start">
                                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                                        onClick={() => handleRequestQuote(pkg.name)}
                                    >
                                        Tanya Paket Ini
                                    </Button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact CTA */}
            <section className="py-20 bg-emerald-900">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="font-display text-3xl font-medium text-white mb-4">Siap Belajar Table Manner?</h2>
                    <p className="text-emerald-200 mb-8">
                        Hubungi tim kami untuk konsultasi dan penawaran khusus untuk grup Anda
                    </p>
                    <a
                        href="https://wa.me/6281130700206?text=Hi,%20saya%20ingin%20bertanya%20tentang%20paket%20Table%20Manner"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center bg-white text-emerald-800 px-8 py-4 rounded-full font-semibold hover:bg-emerald-50 transition-colors"
                        onClick={() => trackWhatsAppClick('Table Manner Page CTA')}
                        data-testid="table-manner-contact-btn"
                    >
                        <Phone className="w-5 h-5 mr-2" />
                        Hubungi Kami
                    </a>
                </div>
            </section>

            {/* Etiquette Video Modal */}
            <EtiquetteVideoModal
                isOpen={showVideoModal}
                onClose={() => setShowVideoModal(false)}
                videoUrl={etiquetteVideoUrl}
            />
        </div>
    );
};

export default TableManner;
